import type { ArtistSummaryRow, PeersResponse } from '@dashboard/contract';
import { fmtBn, fmtPct, growthText } from '../lib/format';

export function PeerPicker({
  artists,
  subjectId,
  peers,
  onChange,
}: {
  artists: ArtistSummaryRow[];
  subjectId: string;
  peers: string[];
  onChange: (peers: string[]) => void;
}) {
  const byId = new Map(artists.map((a) => [a.artistId, a.artistName]));
  const available = artists.filter((a) => a.artistId !== subjectId && !peers.includes(a.artistId));
  return (
    <div className="peerpick">
      {peers.map((id) => (
        <span className="pill" key={id}>
          {byId.get(id) ?? id}
          <button
            type="button"
            aria-label={`remove ${byId.get(id) ?? id}`}
            onClick={() => onChange(peers.filter((p) => p !== id))}
          >
            ×
          </button>
        </span>
      ))}
      <select
        value=""
        aria-label="Add a peer"
        disabled={available.length === 0 || peers.length >= 12}
        onChange={(e) => {
          if (e.target.value) onChange([...peers, e.target.value]);
        }}
      >
        <option value="">{peers.length >= 12 ? 'Peer limit reached' : '+ Add peer…'}</option>
        {available.map((a) => (
          <option key={a.artistId} value={a.artistId}>
            {a.artistName}
          </option>
        ))}
      </select>
    </div>
  );
}

export function PeersTable({ data, sort }: { data: PeersResponse; sort: 'tot' | 'cat' | 'cagr' }) {
  const rows = [...data.rows].sort((a, b) =>
    sort === 'cat'
      ? b.catalogue.ltm - a.catalogue.ltm
      : sort === 'cagr'
        ? (b.catalogue.cagr21 ?? -9) - (a.catalogue.cagr21 ?? -9)
        : b.total.ltm - a.total.ltm,
  );
  const maxTot = Math.max(...rows.map((r) => r.total.ltm), 1);
  const maxCat = Math.max(...rows.map((r) => r.catalogue.ltm), 1);
  const bar = (v: number, max: number, colour: string) => (
    <span className="pbar">
      <span style={{ width: `${((v / max) * 100).toFixed(1)}%`, background: colour }} />
    </span>
  );
  const g = (v: number | null) => {
    const t = growthText(v);
    return <span className={t.cls}>{t.text}</span>;
  };
  return (
    <div className="scroll">
      <table id="t-peers">
        <thead>
          <tr>
            <th className="name">Artist</th>
            <th>CM rank</th>
            <th colSpan={2}>Total streams, LTM</th>
            <th>vs LTM-1</th>
            <th>21-LTM CAGR</th>
            <th colSpan={2}>Catalogue streams, LTM</th>
            <th>Share</th>
            <th>vs LTM-1</th>
            <th>21-LTM CAGR</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.artistId} className={r.isSubject ? 'me' : ''}>
              <td className="name" title={r.artistName}>
                {r.artistName}
                {r.hasOverrides ? null : (
                  <span
                    className="ovr"
                    title="no researched release dates: catalogue split is indicative"
                  >
                    ~
                  </span>
                )}
                {r.anchorDiffers ? (
                  <span
                    className="warnbadge"
                    title={`computed at ${r.anchor}, not the subject's anchor`}
                  >
                    anchor {r.anchor}
                  </span>
                ) : null}
              </td>
              <td>{r.cmRank == null ? '—' : `#${r.cmRank}`}</td>
              <td className="num">{fmtBn(r.total.ltm)}</td>
              <td className="bar">{bar(r.total.ltm, maxTot, 'var(--g1)')}</td>
              <td>{g(r.total.vsPrior)}</td>
              <td>{g(r.total.cagr21)}</td>
              <td className="num">{fmtBn(r.catalogue.ltm)}</td>
              <td className="bar">{bar(r.catalogue.ltm, maxCat, 'var(--accent)')}</td>
              <td>{fmtPct(r.catalogue.share)}</td>
              <td>{g(r.catalogue.vsPrior)}</td>
              <td>{g(r.catalogue.cagr21)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
